/**
 * Newsletter Email Subscription Component
 * ========================================
 * Email opt-in uses Shopify's native Liquid `{% form 'customer' %}` submission
 * (a full-page POST to /contact). Shopify creates the customer, sets email
 * marketing consent, and redirects back to the current page with
 * `?customer_posted=true`. The section renders the success/error message
 * server-side via `form.posted_successfully?` / `form.errors`.
 *
 * This component only layers the secondary integrations on top of that native
 * flow (it never calls `preventDefault`, so Shopify's subscription + captcha
 * keep working):
 *
 *   1. On submit, it stores the entered email + Canada consent in sessionStorage
 *      (the inputs are wiped by the page reload that Shopify performs).
 *   2. After the redirect, if the form posted successfully (Liquid renders
 *      `.newsletter-subscription__success.is-visible`), it reads the stored data
 *      and fires Klaviyo + app proxy + Dynamic Yield, then clears storage.
 *
 * @see https://shopify.dev/docs/storefronts/themes/customer-engagement/email-consent
 */

const PENDING_TTL_MS = 5 * 60 * 1000;

class NewsletterEmail extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    /** @type {HTMLFormElement | null} */
    this.form = this.querySelector('[data-newsletter-form="email"]');
    /** @type {HTMLInputElement | null} */
    this.emailInput = this.querySelector('input[name="contact[email]"]');
    /** @type {HTMLInputElement | null} */
    this.canadaCheckbox = this.querySelector('[data-canada-consent]');
    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector('.newsletter-subscription__button');

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }

    this.#handleServerResponse();
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
    }
  }

  get #config() {
    return {
      sectionId: this.dataset.sectionId || '',
      proxyUrl: this.dataset.proxyUrl || '',
      klaviyoCompanyId: this.dataset.klaviyoCompanyId || '',
      klaviyoListId: this.dataset.klaviyoListId || '',
      klaviyoSource: this.dataset.klaviyoSource || 'Website_Newsletter',
      customerId: this.dataset.customerId || '',
    };
  }

  get #storageKey() {
    return `newsletter-email-pending:${this.#config.sectionId}`;
  }

  /**
   * Persist the pending subscription before the native form navigates away and
   * show a brief loading state. No `preventDefault` — Shopify handles the POST.
   */
  handleSubmit() {
    if (!this.emailInput) return;

    const email = this.emailInput.value.trim();
    if (!email) return;

    const pending = {
      email,
      canadaConsent: this.canadaCheckbox ? this.canadaCheckbox.checked : false,
      ts: Date.now(),
    };

    try {
      sessionStorage.setItem(this.#storageKey, JSON.stringify(pending));
    } catch (error) {
      // sessionStorage may be unavailable (private mode); native submit still works.
    }

    if (this.button) {
      this.button.classList.add('is-loading');
      this.button.disabled = true;
    }
  }

  /**
   * React to the server result after the page reloads from a native submit.
   */
  #handleServerResponse() {
    const success = /** @type {HTMLElement | null} */ (this.querySelector('.newsletter-subscription__success.is-visible'));
    const error = /** @type {HTMLElement | null} */ (this.querySelector('.newsletter-subscription__error.is-visible'));

    if (error) {
      this.#focusMessage(error);
      this.#clearPending();
      return;
    }

    if (!success) {
      this.#clearStalePending();
      return;
    }

    this.#focusMessage(success);

    const pending = this.#readPending();
    this.#clearPending();
    if (!pending || !pending.email) return;

    this.#runIntegrations(pending.email, pending.canadaConsent === true);
  }

  /**
   * Fire secondary integrations once the Shopify subscription has succeeded.
   * Failures here never affect the (already completed) Shopify subscription.
   * @param {string} email
   * @param {boolean} canadaConsent
   */
  async #runIntegrations(email, canadaConsent) {
    const { klaviyoCompanyId, klaviyoListId, klaviyoSource, proxyUrl, customerId } = this.#config;

    if (klaviyoCompanyId && klaviyoListId) {
      try {
        await subscribeToKlaviyo({ email, canadaConsent, companyId: klaviyoCompanyId, listId: klaviyoListId, source: klaviyoSource });
      } catch (error) {
        console.error('Newsletter Klaviyo subscription error:', error);
      }
    }

    if (proxyUrl) {
      try {
        await sendToProxy({ proxyUrl, value: email, consent: canadaConsent, customerId });
      } catch (error) {
        console.error('Newsletter proxy subscription error:', error);
      }
    }

    const win = /** @type {any} */ (window);
    if (typeof win.dyTrackNewsletterSubscription === 'function') {
      try {
        win.dyTrackNewsletterSubscription(email, { canadaConsent });
      } catch (error) {
        console.error('Newsletter DY tracking error:', error);
      }
    }
  }

  /**
   * Move focus to a status/error message for accessibility.
   * @param {HTMLElement} element
   */
  #focusMessage(element) {
    element.setAttribute('tabindex', '-1');
    requestAnimationFrame(() => {
      element.focus();
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /** @returns {{ email: string, canadaConsent: boolean, ts: number } | null} */
  #readPending() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  #clearPending() {
    try {
      sessionStorage.removeItem(this.#storageKey);
    } catch (error) {
      // no-op
    }
  }

  /** Drop pending data older than the TTL so it never fires on an unrelated reload. */
  #clearStalePending() {
    const pending = this.#readPending();
    if (!pending) return;
    if (!pending.ts || Date.now() - pending.ts > PENDING_TTL_MS) {
      this.#clearPending();
    }
  }
}

/**
 * Subscribe a profile to a Klaviyo list using the client-side subscriptions API.
 * @param {{ email: string, canadaConsent: boolean, companyId: string, listId: string, source: string }} options
 * @returns {Promise<Response>}
 */
const subscribeToKlaviyo = async ({ email, canadaConsent, companyId, listId, source }) => {
  const payload = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: source,
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: { canada_consent: canadaConsent },
            },
          },
        },
      },
      relationships: {
        list: { data: { type: 'list', id: listId } },
      },
    },
  };

  const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      revision: '2024-02-15',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok && response.status !== 202) {
    throw new Error('Klaviyo subscription failed');
  }

  return response;
};

/**
 * Forward the signup to a custom app proxy for backend sync.
 * @param {{ proxyUrl: string, value: string, consent: boolean, customerId: string }} options
 * @returns {Promise<any>}
 */
const sendToProxy = async ({ proxyUrl, value, consent, customerId }) => {
  const payload = {
    type: 'email',
    value,
    consent,
    customer_id: customerId || null,
    timestamp: new Date().toISOString(),
  };

  const response = await fetch(proxyUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Proxy request failed');
  }

  return response.json();
};

if (!customElements.get('newsletter-email')) {
  customElements.define('newsletter-email', NewsletterEmail);
}

export {};
